import { BusinessClock } from '../time/business-clock.service';
import { JstDate } from '../time/business-time';
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class SfappService {
  private businessClock = inject(BusinessClock);
  private http = inject(HttpClient);
  private router = inject(Router);
  private base = environment.apiBaseUrl;
  private timeout = environment.timeout;

  private async get<T>(url: string): Promise<T> {
    try {
      const res = await firstValueFrom(this.http.get<any>(url));
      if (res.success !== true) { this.router.navigate(['/error']); return null as T; }
      return res.result ?? res.data;
    } catch {
      this.router.navigate(['/error']);
      return null as T;
    }
  }

  private async post<T>(url: string, body: any): Promise<T> {
    return firstValueFrom(this.http.post<T>(url, body));
  }

  getStudioList(): Promise<any[]> {
    return this.get(`${this.base}/studio/available`);
  }

  getStudioPlanList(studioId: number | string): Promise<any[]> {
    return this.get(`${this.base}/studio/${studioId}/plan/available`);
  }

  getFirstInvoice(studioPlanId: number, startDate: string, paymentMethod: string): Promise<any> {
    return this.get(`${this.base}/studio/plan/${studioPlanId}/start_date/${startDate}/${paymentMethod}`);
  }

  isDisplayed(studioId: number | string): Promise<any> {
    return this.get(`${this.base}/studio/${studioId}/is_displayed`);
  }

  getAddress(zip: string): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.base}/member/zipcode/${zip}`))
      .then((r) => r.result);
  }

  checkAddress(json: any): Promise<any> {
    return this.post(`${this.base}/member/address`, json);
  }

  createMember(json: any): Promise<any> {
    return this.post(`${this.base}/member/new`, json);
  }

  createTemporaryMember(json: any): Promise<any> {
    return this.post(`${this.base}/member/temp/new`, json);
  }

  getLocalNumberList(): Promise<any[]> {
    return this.get(`${this.base}/studio/local`);
  }

  studioExists(studioId: string | number, studioName: string): Promise<any> {
    return this.post(`${this.base}/studio/exists`, { id: studioId, name: studioName })
      .then((r: any) => r?.result)
      .catch(() => {
        this.router.navigate(['/error']);
        return false;
      });
  }

  async getQuestionary(): Promise<any[]> {
    const response = await firstValueFrom(this.http.get<any>(`${this.base}/questionary`).pipe(timeout(15000)));
    if (response?.success !== true || !Array.isArray(response.result)) throw new Error('アンケートを取得できませんでした。');
    return response.result;
  }

  getTempMember(memberId: string, password: string): Promise<any> {
    return this.post(`${this.base}/member/temp/get`, { memberId, password });
  }

  registerTempMember(json: any, pass: string): Promise<any> {
    return this.post(`${this.base}/member/temp/register/${pass}`, json);
  }

  createBtPostValues(memberId?: number | string, paymentMethod?: string): Promise<any[] | null> {
    const body: any = { memberId };
    if (paymentMethod !== undefined && paymentMethod !== null && paymentMethod !== '') {
      body.paymentMethod = paymentMethod;
    }
    return this.post(`${this.base}/member/bt_post_values`, body).then((r: any) => r?.result ?? null);
  }

  getPaymentsReturnResult(resultId: string, memberId: string): Promise<string> {
    return firstValueFrom(
      this.http.get<any>(`${this.base}/payments/return/result`, {
        params: { resultId, memberId },
      })
    ).then((r) => r?.message);
  }

  deleteExpiredPassword(): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.base}/member/temp/pass/delete`));
  }

  uploadPicture(id: number, pictureId: number, key: string, fileData: FormData): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.base}/member/${id}/picture/${pictureId}/key/${key}`, fileData)
    ).then((r) => r?.result);
  }

  async getDate(): Promise<any> {
    if (!await this.businessClock.synchronize()) return null;
    const d = new JstDate();
    return { arr: [d.getFullYear(), d.getMonth()+1, d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds()].map(String), weekday: d.getDay() };
  }

  getVisitMenu(studioId: number | string): Promise<any[]> {
    return firstValueFrom(this.http.get<any>(`${this.base}/reservation/menu/visit/${studioId}`))
      .then((r) => r?.result ?? []);
  }

  getReserveConfig(): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.base}/reservation/config`))
      .then((r) => r?.result);
  }

  getAvailabilityForReserve(id: number, slot: number, count: number, isPt: number): Promise<any[]> {
    const url = `${this.base}/reservation/schedule/studio/${id}/slot/${slot}/reserve_count/${count}/type/${isPt}`;
    return firstValueFrom(this.http.get<any>(url)).then((r) => {
      if (r?.success !== true) return [];
      return r.result ?? [];
    }).catch(() => []);
  }

  createVisitReserve(json: any): Promise<any> {
    // 通信失敗は呼び出し元のcatchへ渡し、予約結果や表示メッセージとして扱わない。
    return this.post(`${this.base}/reservation/record/visit/new`, json).then((r: any) => {
      if (r?.success !== true) return r;
      return r.result;
    });
  }

  removeTempReserve(ids: number[]): Promise<any> {
    if (!ids?.length) return Promise.resolve(null);
    return firstValueFrom(this.http.get<any>(`${this.base}/reservation/record/visit/delete/${ids.join(',')}`))
      .then((r) => r?.result);
  }

  getReservationGlobalFlag(): Promise<any> {
    return firstValueFrom(this.http.get<any>(`${this.base}/reservation/flag/member`))
      .then((r) => r?.result);
  }
}
